import React, { useState } from 'react';
import {
  Linkedin,
  Twitter,
  Mail,
  Users,
  Target,
  Zap,
  Heart,
  Award,
  Briefcase,
  Code,
  TrendingUp,
  MapPin,
  Globe,
  Shield,
  Rocket,
  X,
  ChevronRight,
  Star,
  Quote
} from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';

type Executive = {
  name: string;
  role: string;
  image: string;
  bio: string;
  education: string;
  previous: string;
  linkedin: string;
  twitter: string;
  email: string;
  tenure?: string;
  highlight?: string;
};

const Leadership: React.FC = () => {
  const [selectedExecutive, setSelectedExecutive] = useState<Executive | null>(null);

  const executives: Executive[] = [
    {
      name: 'Jayaprasad (JP) Rao',
      role: 'Chief Executive Officer',
      image: '/team/Jayaprasad.png',
      bio: 'A seasoned business leader with 20+ years of experience, including 18 years in Banking and Digital Transformation. JP brings deep domain expertise in credit, risk, and large-scale financial operations. He has led product management, loan sales, and recovery strategies across secured and unsecured business loans, mortgages, and agri-finance. At ICICI Bank, he played a pivotal role in scaling business lending in rural and semi-urban markets. Recently, he has built low-code, scalable platforms for end-to-end loan journeys. Combines deep banking expertise, technology orientation, and execution strength to build scalable, high-impact ventures.',
      education: "Master's in International Business from IIFT, New Delhi",
      previous: 'Awarded DNA Award for Sensitivity at ICICI Bank',
      linkedin: '#',
      twitter: '#',
      email: 'info@gaadimatic.com',
      tenure: '20+ Years Experience',
      highlight: 'Built ICICI\'s Rural Lending Scale'
    },
    {
      name: 'Prasad Kande',
      role: 'Chief Operating Officer',
      image: '/team/prasadkande.png',
      bio: "Prasad brings over 15 years of experience building, scaling, and operating technology businesses in India. As the former CEO of Telvi Technologies, he built and led a team that developed enterprise-grade fleet and logistics solutions, earning the trust of customers across multiple states. His transition to COO of GaadiMatic reflects a hands-on conviction — he believes great products are built by those who stay close to operations. At GaadiMatic, Prasad oversees the end-to-end customer journey: from onboarding fleet operators and streamlining vehicle onboarding to ensuring delivery excellence and customer success. He is deeply invested in making sure every fleet operator's experience with GaadiMatic is smoother, faster, and more profitable than anything they used before.",
      education: '',
      previous: 'Former CEO, Telvi Technologies',
      linkedin: '#',
      twitter: '#',
      email: '',
      tenure: '15+ Years Experience',
      highlight: 'Built & Scaled Fleet Tech Solutions at Telvi'
    },
    {
      name: 'Swamy Sirigada',
      role: 'Chief Financial Officer',
      image: '/team/swamy.png',
      bio: 'Swamy is a seasoned finance professional and mobility industry leader with over 18 years of experience, including 12+ years in transportation and logistics. His expertise spans Employee Transportation Services, spot bookings, intercity mobility, goods transport, and last-mile delivery solutions. As the Founder of Phoenitech Travels, Swamy successfully built and scaled the organization into a top transport service provider in Hyderabad. At GaadiMatic, he brings a strategic financial perspective combined with deep domain knowledge in mobility operations. He is a strong advocate of technology-led transformation in logistics.',
      education: '',
      previous: 'Founder of Phoenitech Travels',
      linkedin: '#',
      twitter: '#',
      email: '',
      tenure: '18+ Years Experience',
      highlight: 'Scaled Phoenitech Travels to Top Transport Provider'
    },
    {
      name: 'Srinivasulu ',
      role: 'Chief Technology Officer',
      image: '/team/srinivasulu.png',
      bio: "Srinu is a systems architect with 14 years of experience spanning the full technology stack — from Java and Python backend services to PHP and React-driven frontends. He has deep expertise in DevOps, infrastructure scaling, and building high-availability systems that handle thousands of concurrent operations. Before joining GaadiMatic, he led security-first engineering initiatives, designing scalable DevOps pipelines and hardening systems against modern threats at scale. At GaadiMatic, he is the architectural backbone — ensuring our fleet platform is built for reliability, speed, and enterprise-grade security. He believes that in logistics, downtime is money lost, and engineers every system with that reality in mind.",
      education: '',
      previous: 'Security-First Engineering & DevOps at Scale',
      linkedin: '#',
      twitter: '#',
      email: '',
      tenure: '14+ Years Experience',
      highlight: 'Architected High-Availability Systems at Scale'
    }
  ];

  const departmentLeaders = [
    {
      name: 'Phani Kumar',
      role: 'Product Owner',
      department: 'Product',
      image: '',
      bio: 'Drives product vision and roadmap execution across GaadiMatic\'s fleet platform, bridging customer needs with engineering delivery.'
    },
    {
      name: 'Vanaja Garikapati',
      role: 'Frontend Lead',
      department: 'Engineering',
      image: '',
      bio: 'Leads the frontend engineering team, crafting intuitive and performant interfaces for fleet operators and drivers.'
    },
    {
      name: 'Ananth Golla',
      role: 'Fullstack Engineer',
      department: 'Engineering',
      image: '',
      bio: 'Full-stack engineer building end-to-end features across the GaadiMatic platform with a focus on reliability and scale.'
    },
    {
      name: 'Raja Pallapu',
      role: 'Mobile Lead',
      department: 'Engineering',
      image: '',
      bio: 'Leads mobile application development for driver and operator apps, delivering seamless cross-platform experiences.'
    },
  ];

  const advisors = [
    
    {
      name: 'Ramakrishnan L',
      role: 'Transport Advisory',
      department: 'Operations & Advisory',
      image: '/team/Ramakrishnan.jpeg',
      bio: 'Seasoned transport industry expert advising GaadiMatic on fleet operations strategy and ground-level logistics realities across India.'
    },
    {
      name: 'Vinayak Hiremath',
      role: 'Finance & Compliance',
      department: 'Finance Advisory',
      image: '/team/vinay.jpeg',
      bio: 'Partner at Hiremath & Rathi, Chartered Accountants. Expert in accounting, taxation, business planning, valuation, and investor relations.'
    },
    {
      name: 'Chenchu Y',
      role: 'Technology Advisory',
      department: 'Technology',
      image: '/team/chenchu.png',
      bio: 'CTO of Telvi Technologies. Brings deep expertise in fleet tech product architecture and enterprise software delivery.'
    },
  ];

  const cultureValues = [
    {
      title: 'Customer First',
      description: 'Every decision starts with our customers. We visit fleet operators, ride along with drivers, and deeply understand their pain points.',
      icon: Heart
    },
    {
      title: 'Move Fast',
      description: 'Speed is our competitive advantage. We ship features weekly, iterate based on feedback, and are not afraid to fail fast.',
      icon: Zap
    },
    {
      title: 'Think Big',
      description: 'We are building the operating system for fleet management in India. Every solution is designed to scale.',
      icon: Target
    },
    {
      title: 'Stay Grounded',
      description: 'We are building for Bharat - for drivers, fleet operators, and logistics workers. We stay humble and grounded in reality.',
      icon: MapPin
    }
  ];

  const teamStats = [
    { value: '20+', label: 'Team Members', icon: Users },
    { value: '5+', label: 'Departments', icon: Briefcase },
    { value: '15+', label: 'Engineers', icon: Code },
    { value: '3', label: 'Founding Team', icon: Rocket }
  ];

  const [ceoExpanded, setCeoExpanded] = useState(false);
  const [cfoExpanded, setCfoExpanded] = useState(false);
  const [techCfoExpanded, setTechCfoExpanded] = useState(false);
  const [techCtoExpanded, setTechCtoExpanded] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <AnimatedBackground />

      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-24 overflow-hidden bg-gradient-to-b from-navy-950 via-navy-900 to-navy-950">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-96 h-96 bg-primary-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-amber-500 rounded-full blur-3xl" />
        </div>
        <div className="container-main relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 border border-white/10">
              <Star className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium text-white/90">The Leadership Behind GaadiMatic</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Built by Industry <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500">Veterans</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/70 leading-relaxed max-w-2xl mx-auto">
              Seasoned operators from banking, logistics, and technology who've built and scaled businesses — now building India's fleet operating system.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white border-b border-gray-100">
        <div className="container-main">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {teamStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-6 h-6 text-primary-600" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-navy-950 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50/50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Executive Leadership
            </h2>
            <p className="text-lg text-gray-600">
              The C-suite team driving GaadiMatic's mission to transform fleet management in India.
            </p>
          </div>

          <div className="space-y-6 max-w-6xl mx-auto">

            {executives.slice(0, 2).map((exec, index) => {
              const isEven = index % 2 === 0;
              const isExpanded = exec.name.includes('JP') ? ceoExpanded : cfoExpanded;

              return (
                <div
                  key={index}
                  className={`group relative overflow-hidden rounded-3xl bg-white border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-500 ${isExpanded ? 'ring-2 ring-primary-200' : ''}`}
                >
                  <div className="flex flex-col lg:flex-row">
                    <div className={`lg:w-2/5 relative ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                      <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-navy-900" />
                      <div className="relative p-8 lg:p-12 flex flex-col items-center justify-center min-h-80 lg:min-h-96">
                        <div className="w-36 h-36 lg:w-44 lg:h-44 rounded-2xl overflow-hidden shadow-2xl mb-6 ring-4 ring-white/20 transform group-hover:scale-105 transition-transform duration-500">
                          {exec.image.startsWith('/') ? (
                            <img
                              src={exec.image}
                              alt={exec.name}
                              className="w-full h-full object-cover object-top"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                              <span className="text-4xl font-bold text-white">{exec.image}</span>
                            </div>
                          )}
                        </div>
                        <div className="text-center">
                          <h3 className="text-2xl font-bold text-white mb-1">{exec.name}</h3>
                          <p className="text-primary-200 font-medium text-lg">{exec.role}</p>
                        </div>
                      </div>
                    </div>

                    <div className={`lg:w-3/5 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                      <div className="p-8 lg:p-12 flex flex-col justify-center h-full">
                        {exec.highlight && (
                          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-50 text-amber-700 text-sm font-semibold rounded-full mb-4 w-fit">
                            <Quote className="w-3 h-3" />
                            {exec.highlight}
                          </div>
                        )}

                        <p className="text-gray-600 leading-relaxed mb-6 text-base">
                          {isExpanded ? exec.bio : exec.bio.slice(0, 280) + '...'}
                        </p>

                        <div className="flex flex-wrap gap-4 mb-6">
                          {exec.tenure && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Briefcase className="w-4 h-4 text-primary-500" />
                              {exec.tenure}
                            </div>
                          )}
                          {exec.education && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Award className="w-4 h-4 text-primary-500" />
                              {exec.education}
                            </div>
                          )}
                          {exec.previous && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <TrendingUp className="w-4 h-4 text-primary-500" />
                              {exec.previous}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="flex gap-2">
                            {exec.linkedin && (
                              <a href={exec.linkedin} className="w-10 h-10 bg-navy-900 hover:bg-primary-600 rounded-full flex items-center justify-center transition-colors">
                                <Linkedin className="w-5 h-5 text-white" />
                              </a>
                            )}
                            {exec.twitter && (
                              <a href={exec.twitter} className="w-10 h-10 bg-navy-900 hover:bg-primary-600 rounded-full flex items-center justify-center transition-colors">
                                <Twitter className="w-5 h-5 text-white" />
                              </a>
                            )}
                            {exec.email && (
                              <a href={`mailto:${exec.email}`} className="w-10 h-10 bg-navy-900 hover:bg-primary-600 rounded-full flex items-center justify-center transition-colors">
                                <Mail className="w-5 h-5 text-white" />
                              </a>
                            )}
                          </div>

                          <button
                            onClick={() => {
                              if (exec.name.includes('JP')) setCeoExpanded(!ceoExpanded);
                              else setCfoExpanded(!cfoExpanded);
                            }}
                            className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold text-sm transition-colors"
                          >
                            {isExpanded ? 'Show Less' : 'Read Full Bio'}
                            <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Technology & Operations
            </h2>
            <p className="text-lg text-gray-600">
              The technical and operational leaders powering GaadiMatic's platform.
            </p>
          </div>

          <div className="space-y-6 max-w-6xl mx-auto">
            {executives.slice(2).map((exec, index) => {
              const isEven = index % 2 === 0;
              const isExpanded = exec.name.includes('Swamy') ? techCfoExpanded : techCtoExpanded;

              return (
                <div
                  key={index}
                  className={`group relative overflow-hidden rounded-3xl bg-white border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-500 ${isExpanded ? 'ring-2 ring-primary-200' : ''}`}
                >
                  <div className="flex flex-col lg:flex-row">
                    <div className={`lg:w-2/5 relative ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                      <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-700 to-navy-900" />
                      <div className="relative p-8 lg:p-12 flex flex-col items-center justify-center min-h-80 lg:min-h-96">
                        <div className="w-36 h-36 lg:w-44 lg:h-44 rounded-2xl overflow-hidden shadow-2xl mb-6 ring-4 ring-white/20 transform group-hover:scale-105 transition-transform duration-500">
                          {exec.image.startsWith('/') ? (
                            <img
                              src={exec.image}
                              alt={exec.name}
                              className="w-full h-full object-cover object-top"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                              <span className="text-4xl font-bold text-white">{exec.image}</span>
                            </div>
                          )}
                        </div>
                        <div className="text-center">
                          <h3 className="text-2xl font-bold text-white mb-1">{exec.name}</h3>
                          <p className="text-primary-200 font-medium text-lg">{exec.role}</p>
                        </div>
                      </div>
                    </div>

                    <div className={`lg:w-3/5 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                      <div className="p-8 lg:p-12 flex flex-col justify-center h-full">
                        {exec.highlight && (
                          <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-50 text-amber-700 text-sm font-semibold rounded-full mb-4 w-fit">
                            <Quote className="w-3 h-3" />
                            {exec.highlight}
                          </div>
                        )}

                        <p className="text-gray-600 leading-relaxed mb-6 text-base">
                          {isExpanded ? exec.bio : exec.bio.slice(0, 280) + '...'}
                        </p>

                        <div className="flex flex-wrap gap-4 mb-6">
                          {exec.tenure && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Briefcase className="w-4 h-4 text-primary-500" />
                              {exec.tenure}
                            </div>
                          )}
                          {exec.education && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Award className="w-4 h-4 text-primary-500" />
                              {exec.education}
                            </div>
                          )}
                          {exec.previous && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <TrendingUp className="w-4 h-4 text-primary-500" />
                              {exec.previous}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="flex gap-2">
                            {exec.linkedin && (
                              <a href={exec.linkedin} className="w-10 h-10 bg-navy-900 hover:bg-primary-600 rounded-full flex items-center justify-center transition-colors">
                                <Linkedin className="w-5 h-5 text-white" />
                              </a>
                            )}
                            {exec.twitter && (
                              <a href={exec.twitter} className="w-10 h-10 bg-navy-900 hover:bg-primary-600 rounded-full flex items-center justify-center transition-colors">
                                <Twitter className="w-5 h-5 text-white" />
                              </a>
                            )}
                            {exec.email && (
                              <a href={`mailto:${exec.email}`} className="w-10 h-10 bg-navy-900 hover:bg-primary-600 rounded-full flex items-center justify-center transition-colors">
                                <Mail className="w-5 h-5 text-white" />
                              </a>
                            )}
                          </div>

                          <button
                            onClick={() => {
                              if (exec.name.includes('Swamy')) setTechCfoExpanded(!techCfoExpanded);
                              else setTechCtoExpanded(!techCtoExpanded);
                            }}
                            className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold text-sm transition-colors"
                          >
                            {isExpanded ? 'Show Less' : 'Read Full Bio'}
                            <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50/50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-navy-950 mb-3">
              Functional Leaders
            </h2>
            <p className="text-base text-gray-600">
              The core team building and shipping GaadiMatic's platform every day.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {departmentLeaders.map((leader, index) => {
              const colors = [
                { bg: 'bg-primary-600', light: 'bg-primary-50', tag: 'bg-primary-100 text-primary-700' },
                { bg: 'bg-violet-600', light: 'bg-violet-50', tag: 'bg-violet-100 text-violet-700' },
                { bg: 'bg-teal-600', light: 'bg-teal-50', tag: 'bg-teal-100 text-teal-700' },
                { bg: 'bg-orange-500', light: 'bg-orange-50', tag: 'bg-orange-100 text-orange-700' },
              ];
              const c = colors[index % colors.length];
              return (
                <div key={index} className={`flex items-start gap-4 p-5 rounded-2xl border border-gray-100 ${c.light} hover:shadow-md transition-all duration-300`}>
                  <div className={`w-12 h-12 ${c.bg} rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <span className="text-lg font-extrabold text-white">{leader.name.charAt(0)}</span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-navy-950 text-base leading-tight">{leader.name}</h3>
                    <p className="text-gray-600 text-sm font-medium mt-0.5">{leader.role}</p>
                    <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full mt-1.5 ${c.tag}`}>{leader.department}</span>
                    <p className="text-gray-700 text-sm leading-relaxed mt-2">{leader.bio}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-navy-950 mb-3">
              Advisory Board
            </h2>
            <p className="text-base text-gray-600">
              Industry veterans providing strategic guidance and expertise.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {advisors.map((advisor, index) => (
              <div key={index} className="card overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="h-20 bg-gradient-to-br from-amber-500 to-amber-700" />
                <div className="px-6 pb-6">
                  <div className="relative -mt-10 mb-4">
                    <div className="w-20 h-20 bg-white rounded-full p-1 mx-auto overflow-hidden shadow-md">
                      <div className="w-full h-full bg-gradient-to-br from-amber-100 to-amber-200 rounded-full overflow-hidden flex items-center justify-center">
                        {advisor.image ? (
                          <img src={advisor.image} alt={advisor.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xl font-bold text-amber-700">{advisor.name.charAt(0)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="font-bold text-navy-950 text-lg mb-1">{advisor.name}</h3>
                    <p className="text-primary-600 text-sm font-medium mb-1">{advisor.role}</p>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-3">{advisor.department}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{advisor.bio}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-navy-950">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our Culture
            </h2>
            <p className="text-lg text-gray-400">
              The values that guide how we work, collaborate, and build together.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {cultureValues.map((value, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 bg-primary-500/20 rounded-xl flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-primary-400" />
                </div>
                <h3 className="font-bold text-white mb-3">{value.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Why Join Our Team?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Be part of a team transforming India's logistics industry. Work on real problems, grow fast, and make an impact that matters.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Zap, color: 'bg-amber-50 text-amber-600', border: 'border-amber-100', title: 'High Impact', desc: 'Your work directly shapes the experience of 500+ fleet operators and 10,000+ vehicles on the road every day.' },
              { icon: TrendingUp, color: 'bg-green-50 text-green-600', border: 'border-green-100', title: 'Rapid Growth', desc: '180% YoY growth means massive career advancement opportunities. We promote from within and invest in our people.' },
              { icon: Code, color: 'bg-primary-50 text-primary-600', border: 'border-primary-100', title: 'Cutting-Edge Tech', desc: 'Work with a modern stack — React, Node.js, AWS, real-time GPS systems, and AI-powered fleet intelligence.' },
              { icon: Shield, color: 'bg-violet-50 text-violet-600', border: 'border-violet-100', title: 'Full Ownership', desc: 'High autonomy from day one. We trust our team to make decisions and own outcomes — no bureaucracy, no red tape.' },
              { icon: Award, color: 'bg-rose-50 text-rose-600', border: 'border-rose-100', title: 'Competitive Pay', desc: 'Market-leading salaries, performance bonuses, and an equity structure that rewards long-term contributors.' },
              { icon: MapPin, color: 'bg-teal-50 text-teal-600', border: 'border-teal-100', title: 'Flexible & Hybrid', desc: 'Hybrid work model based out of Hyderabad. We believe great work happens when people have the freedom to do their best.' },
            ].map((item, index) => (
              <div key={index} className={`bg-white rounded-2xl border ${item.border} p-6 hover:shadow-lg transition-all duration-300`}>
                <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center mb-4`}>
                  <item.icon className="w-6 h-6" />
                </div>
                <h4 className="font-bold text-navy-950 text-base mb-2">{item.title}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary-600">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Build the Future of Fleet Management?
            </h2>
            <p className="text-lg text-primary-100 mb-10">
              Join our team of passionate engineers, designers, and logistics experts.
              We are always looking for exceptional talent to help us transform India's logistics industry.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:careers@gaadimatic.com"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white border-2 border-white/30 rounded-lg transition-all duration-200 hover:bg-white/10"
              >
                <Mail className="w-5 h-5 mr-2" />
                careers@gaadimatic.com
              </a>
            </div>

            <div className="mt-12 pt-8 border-t border-white/20">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-primary-100">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span>Hyderabad, India</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  <span>Hybrid Work Model</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {selectedExecutive && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div
            className="absolute inset-0 bg-navy-950/60 backdrop-blur-sm transition-opacity"
            onClick={() => setSelectedExecutive(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto z-10 animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => setSelectedExecutive(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors z-20"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            <div className="flex flex-col md:flex-row">
              <div className="md:w-2/5 bg-gradient-to-br from-primary-50 to-primary-100 p-8 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-primary-200/50">
                <div className="w-40 h-40 bg-white rounded-full p-1 mx-auto overflow-hidden shadow-md mb-6">
                  <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center overflow-hidden">
                    {selectedExecutive.image.startsWith('/') ? (
                      <img src={selectedExecutive.image} alt={selectedExecutive.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-4xl font-bold text-primary-700">{selectedExecutive.image}</span>
                    )}
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-navy-950 mb-2 text-center">{selectedExecutive.name}</h3>
                <p className="text-primary-700 font-medium text-center mb-6">{selectedExecutive.role}</p>

                <div className="flex justify-center gap-3 w-full">
                  {selectedExecutive.linkedin && selectedExecutive.linkedin !== '#' && (
                    <a href={selectedExecutive.linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white hover:bg-primary-600 hover:text-white rounded-full flex items-center justify-center transition-all shadow-sm text-gray-600">
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                  {selectedExecutive.twitter && selectedExecutive.twitter !== '#' && (
                    <a href={selectedExecutive.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white hover:bg-primary-600 hover:text-white rounded-full flex items-center justify-center transition-all shadow-sm text-gray-600">
                      <Twitter className="w-5 h-5" />
                    </a>
                  )}
                  {selectedExecutive.email && (
                    <a href={`mailto:${selectedExecutive.email}`} className="w-10 h-10 bg-white hover:bg-primary-600 hover:text-white rounded-full flex items-center justify-center transition-all shadow-sm text-gray-600">
                      <Mail className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>

              <div className="md:w-3/5 p-8 md:p-10">
                <h4 className="text-lg font-bold text-navy-950 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary-600" />
                  Biography
                </h4>
                <div className="prose prose-gray max-w-none mb-8">
                  {selectedExecutive.bio.split('\n').map((paragraph, idx) => (
                    <p key={idx} className="text-gray-600 leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </div>

                {(selectedExecutive.education || selectedExecutive.previous) && (
                  <div className="space-y-4 bg-gray-50 p-5 rounded-xl border border-gray-100">
                    {selectedExecutive.education && (
                      <div className="flex items-start gap-3">
                        <Award className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-navy-950">Education & Credentials</p>
                          <p className="text-sm text-gray-600">{selectedExecutive.education}</p>
                        </div>
                      </div>
                    )}
                    {selectedExecutive.previous && (
                      <div className="flex items-start gap-3">
                        <TrendingUp className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-navy-950">Career Highlights</p>
                          <p className="text-sm text-gray-600">{selectedExecutive.previous}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leadership;
